import React from 'react';
import SuperPhilosophyTagline from '../../components/SuperPhilosophyTagline';

// ARCPartnership — UNLISTED proposal page for the Alliance for Responsible Citizenship.
// Reached only via the direct link emailed to ARC. NOT added to PlatformPage's public
// segment grid or any public directory. Links OUT to public pages; nothing links IN.
// Route: /partners/arc

const ARCPartnership = () => {
  const T = {
    ink:       '#142943',
    inkSoft:   '#33455e',
    paper:     '#EEF1F5',
    paperWarm: '#F6F4EE',
    blue:      '#4f46e5',
    red:       '#ef4444',
    green:     '#059669',
    line:      'rgba(20,41,67,0.14)',
  };

  // Public pages an ARC leader can actually read (no gated coach apps —
  // ARC hasn't onboarded, so links to /emit-the-bible etc. would dead-end).
  // One-directional: ARC page → public pages (never the reverse).
  const navLinks = [
    { label: 'Overview', href: 'https://freehappyone.com/platform' },
    { label: 'Partner With Us', href: 'https://freehappyone.com/platform#partnerships' },
  ];

  const go = (href) => { window.location.href = href; };

  return (
    <div style={{ background: T.paper, color: T.ink, fontFamily: "'Inter', system-ui, sans-serif", lineHeight: 1.6, minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&display=swap');
        .arc-h1,.arc-h2,.arc-h3{font-family:'Fraunces',Georgia,serif;font-weight:500;line-height:1.12;letter-spacing:-0.01em;margin:0;}
        .arc-wrap{max-width:1080px;margin:0 auto;padding:0 28px;}
        .arc-eyebrow{font-size:12px;font-weight:600;letter-spacing:0.18em;text-transform:uppercase;color:${T.blue};margin-bottom:18px;}
        .arc-navlink{font-size:14px;font-weight:500;color:${T.inkSoft};text-decoration:none;padding:6px 2px;border-bottom:1px solid transparent;transition:all .15s ease;cursor:pointer;background:none;border-top:none;border-left:none;border-right:none;font-family:inherit;}
        .arc-navlink:hover{color:${T.blue};border-bottom-color:${T.blue};}
        .arc-btn{display:inline-block;font-weight:600;font-size:15px;padding:15px 26px;border-radius:2px;text-decoration:none;transition:transform .15s ease;cursor:pointer;border:none;font-family:inherit;}
        @media (max-width:820px){
          .arc-bridge{grid-template-columns:1fr !important;}
          .arc-span{flex-direction:row !important;gap:14px !important;padding:18px !important;min-width:0 !important;}
          .arc-grid3{grid-template-columns:1fr !important;}
          .arc-pilot{grid-template-columns:1fr !important;padding:44px 30px !important;}
          .arc-nav-inner{flex-wrap:wrap !important;gap:14px !important;}
        }
        @media (prefers-reduced-motion:reduce){*{transition:none !important;}}
      `}</style>

      {/* NAV — links out to public pages */}
      <nav style={{ borderBottom: `1px solid ${T.line}`, background: '#fff' }}>
        <div className="arc-wrap arc-nav-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 28px', gap: '24px' }}>
          <button className="arc-navlink" onClick={() => go('https://freehappyone.com/platform')} style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', fontWeight: 600, color: T.ink, borderBottom: 'none' }}>
            FHO <span style={{ color: T.red }}>Platform</span>
          </button>
          <div style={{ display: 'flex', gap: '22px', alignItems: 'center' }}>
            {navLinks.map(l => (
              <button key={l.label} className="arc-navlink" onClick={() => go(l.href)}>{l.label}</button>
            ))}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section style={{
        background: 'radial-gradient(120% 90% at 82% -10%, rgba(58,79,176,0.10), transparent 60%), linear-gradient(180deg, #fff 0%, ' + T.paper + ' 100%)',
        borderBottom: `1px solid ${T.line}`, padding: '84px 0 76px',
      }}>
        <div className="arc-wrap">
          <p className="arc-eyebrow">A partnership proposal · prepared for ARC's review</p>
          <h1 className="arc-h1" style={{ fontSize: 'clamp(38px,6vw,68px)', maxWidth: '14ch' }}>
            A better world begins with a better <em style={{ fontStyle: 'italic', color: T.red }}>Self</em>.
          </h1>
            <div style={{ marginTop: '18px', marginBottom: '8px' }}>
              <SuperPhilosophyTagline variant="arc" color={T.ink} align="left" />
            </div>
          <p style={{ fontSize: 'clamp(17px,2.2vw,21px)', color: T.inkSoft, maxWidth: '54ch', marginTop: '26px' }}>
            ARC asks how we build a good society. The FHO methodology asks how an individual builds a good Self — and from that Self, contributes meaningfully to the world. Not a competing question. A sequential one.
          </p>
          <div style={{ marginTop: '38px', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
            <a className="arc-btn" href="#pilot" style={{ background: T.ink, color: '#fff', boxShadow: '0 10px 28px -12px rgba(20,41,67,.6)' }}
               onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
              Request the complimentary pilot
            </a>
            <a className="arc-btn" href="#sequence" style={{ color: T.ink, borderBottom: `1px solid ${T.line}`, borderRadius: 0, padding: '15px 4px' }}>
              See how the two connect ↓
            </a>
          </div>
        </div>
      </section>

      {/* SEQUENCE */}
      <section id="sequence" style={{ padding: '88px 0', borderBottom: `1px solid ${T.line}` }}>
        <div className="arc-wrap">
          <p className="arc-eyebrow">The sequence</p>
          <h2 className="arc-h2" style={{ fontSize: 'clamp(26px,3.6vw,40px)', maxWidth: '20ch', marginBottom: '14px' }}>Civic vision needs individual formation.</h2>
          <p style={{ color: T.inkSoft, maxWidth: '58ch', marginBottom: '54px' }}>
            A responsible society is made of responsible selves. ARC works at the level of culture and institutions. FHO works one level upstream — forming the individual whose character makes the civic vision possible.
          </p>
          <div className="arc-bridge" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', border: `1px solid ${T.line}`, borderRadius: '4px', overflow: 'hidden', background: '#fff' }}>
            <div style={{ padding: '40px 34px', background: T.paperWarm }}>
              <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '14px', color: T.red }}>The individual</div>
              <h3 className="arc-h3" style={{ fontSize: '23px', marginBottom: '12px' }}>A good Self</h3>
              <p style={{ color: T.inkSoft, fontSize: '15px' }}>Structured self-development: how a person builds Powerful Beliefs, Positive Feelings, and Productive Actions.</p>
            </div>
            <div className="arc-span" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: T.ink, color: '#fff', padding: '0 30px', minWidth: '150px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', letterSpacing: '0.14em', textTransform: 'uppercase', opacity: .6, marginBottom: '10px' }}>formed by</div>
              <strong style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: '20px', lineHeight: 1.2 }}>The <span style={{ color: '#818cf8' }}>F</span><span style={{ color: '#f87171' }}>H</span><span style={{ color: '#34d399' }}>O</span><br />Methodology</strong>
            </div>
            <div style={{ padding: '40px 34px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '14px', color: T.blue }}>The society</div>
              <h3 className="arc-h3" style={{ fontSize: '23px', marginBottom: '12px' }}>A good society</h3>
              <p style={{ color: T.inkSoft, fontSize: '15px' }}>ARC's work: culture, institutions, and the civic responsibility that a flourishing society depends on.</p>
            </div>
          </div>
          <p style={{ marginTop: '30px', fontSize: '15px', color: T.inkSoft, maxWidth: '60ch' }}>
            The methodology is the bridge between the two. <b style={{ color: T.ink, fontWeight: 600 }}>It produces the kind of responsible citizen ARC's mission envisions</b> — not by argument, but by formation.
          </p>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section style={{ padding: '88px 0', borderBottom: `1px solid ${T.line}` }}>
        <div className="arc-wrap">
          <p className="arc-eyebrow">What the methodology forms</p>
          <h2 className="arc-h2" style={{ fontSize: 'clamp(26px,3.6vw,40px)', marginBottom: '46px', maxWidth: '18ch' }}>Three MindStreams of Development.</h2>
          <div className="arc-grid3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1px', background: T.line, border: `1px solid ${T.line}` }}>
            {[
              { k: 'Power', kc: T.blue, h: 'Freedom with THE Source', p: 'The capacity to access divine wisdom and correct misaligned Beliefs, Thoughts, and Ideas for a Divine or Elevated Vision.' },
              { k: 'Flow', kc: T.red, h: 'Happiness within Self', p: 'The ability to govern your Feelings, Emotions, and Commitments for Creation.' },
              { k: 'Clarity', kc: T.green, h: 'Oneness with Others', p: 'How your formed inner life expresses outward through Actions, Behaviors, and Accountability for Desired Outcomes.' },
            ].map(c => (
              <div key={c.k} style={{ background: T.paper, padding: '34px 28px' }}>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: '15px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '14px', color: c.kc }}>{c.k}</div>
                <h3 className="arc-h3" style={{ fontSize: '19px', marginBottom: '10px', fontWeight: 500 }}>{c.h}</h3>
                <p style={{ fontSize: '14.5px', color: T.inkSoft }}>{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section style={{ padding: '88px 0', borderBottom: `1px solid ${T.line}`, background: T.paperWarm }}>
        <div className="arc-wrap">
          <p className="arc-eyebrow">Built, not theoretical</p>
          <h2 className="arc-h2" style={{ fontSize: 'clamp(26px,3.6vw,40px)', maxWidth: '20ch', marginBottom: '14px' }}>The FHO Platform is live — evaluate it through experience.</h2>
          <p style={{ color: T.inkSoft, maxWidth: '56ch', marginBottom: '44px' }}>
            This is not a concept deck. It is a working platform with self-assessment, growth tracking, and a guided development path. Pilot members would use the real tools, not a demo.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${T.line}` }}>
            {[
              { n: '01', h: 'FHO-5 Check-In', p: "A five-question measure of an individual's current state across the methodology — a clear starting point that makes growth visible from the first session." },
              { n: '02', h: 'Development path', p: 'A structured progression that tracks growth over time rather than a one-time score, so members see formation unfold across Power, Flow, and Clarity.' },
              { n: '03', h: 'Guided practice', p: 'Daily tools for self-reflection, growth tracking, and purposeful living — the methodology applied, not merely described.' },
            ].map(c => (
              <div key={c.n} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '30px', alignItems: 'baseline', padding: '30px 0', borderBottom: `1px solid ${T.line}` }}>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 500, color: T.red }}>{c.n}</div>
                <div>
                  <h3 className="arc-h3" style={{ fontSize: '21px', fontWeight: 500, marginBottom: '8px' }}>{c.h}</h3>
                  <p style={{ color: T.inkSoft, fontSize: '15.5px', maxWidth: '60ch' }}>{c.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILOT */}
      <section id="pilot" style={{ padding: '96px 0' }}>
        <div className="arc-wrap">
          <div className="arc-pilot" style={{ background: T.ink, color: '#fff', borderRadius: '6px', padding: '60px 54px', display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '48px', alignItems: 'center' }}>
            <div>
              <h2 className="arc-h2" style={{ color: '#fff', fontSize: 'clamp(26px,3.4vw,38px)', maxWidth: '18ch' }}>A complimentary pilot for ARC members.</h2>
              <p style={{ color: 'rgba(255,255,255,.78)', marginTop: '18px', fontSize: '16px', maxWidth: '46ch' }}>
                Full access to the FHO Platform, at no cost to ARC, so a small group can test a single premise: that systematic self-development forms individuals more equipped to engage responsibly in society and civic life.
              </p>
              <a className="arc-btn" href="mailto:partnerships@freehappyone.com?subject=FHO%20%C3%97%20ARC%20%E2%80%94%20Proposed%20Partnership%20%26%20Member%20Pilot&body=Dear%20Carlton%2C%0A%0AWe%27ve%20reviewed%20the%20FHO%20partnership%20proposal%20and%20would%20like%20to%20discuss%20the%20complimentary%20member%20pilot.%0A%0AName%3A%0ARole%20at%20ARC%3A%0ABest%20way%20to%20reach%20us%3A%0A%0A"
                 style={{ background: '#fff', color: T.ink, marginTop: '22px' }}
                 onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
                Start the conversation
              </a>
            </div>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px', padding: 0, margin: 0 }}>
              {['No cost, no obligation', 'Flexible format, timeline, and group size', 'Full platform access for the pilot group', "Evaluate alignment with ARC's mission directly"].map(item => (
                <li key={item} style={{ paddingLeft: '24px', position: 'relative', fontSize: '15px', color: 'rgba(255,255,255,.9)' }}>
                  <span style={{ position: 'absolute', left: 0, top: '9px', width: '9px', height: '9px', background: T.red, borderRadius: '50%' }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: `1px solid ${T.line}`, padding: '38px 0', color: T.inkSoft, fontSize: '13.5px' }}>
        <div className="arc-wrap" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
          <span>FHO Methodology · MindStreams Technology</span>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
            {navLinks.map(l => (
              <button key={l.label} className="arc-navlink" onClick={() => go(l.href)} style={{ fontSize: '13.5px' }}>{l.label}</button>
            ))}
          </div>
        </div>
        <div className="arc-wrap" style={{ marginTop: '14px' }}>
          <span style={{ fontSize: '12.5px', opacity: .8 }}>
            This page outlines a proposed collaboration prepared for ARC's consideration. FHO / MindStreams Technology and the Alliance for Responsible Citizenship are not currently affiliated.
          </span>
        </div>
      </footer>
    </div>
  );
};

export default ARCPartnership;
